<template>
  <div class="field-insights-panel">
    <template v-if="!selectedUser">
      <div class="panel-header">
        <span class="panel-title">Usuarios</span>
        <span class="panel-subtitle">Últimos 45 días</span>
      </div>

      <div
        v-if="!loadingUsers && !errorUsers && users.length"
        class="search-wrap"
      >
        <i class="fa fa-search search-icon" />
        <input
          v-model="userSearchQuery"
          type="search"
          class="user-search-input"
          placeholder="Buscar usuario…"
          autocomplete="off"
        >
        <button
          v-if="userSearchQuery"
          type="button"
          class="search-clear"
          aria-label="Limpiar búsqueda"
          @click="userSearchQuery = ''"
        >
          <i class="fa fa-times" />
        </button>
      </div>

      <div class="panel-body">
        <div
          v-if="loadingUsers"
          class="user-list-skeleton"
        >
          <div
            v-for="i in 6"
            :key="i"
            class="user-row-skeleton shimmer"
          />
        </div>

        <div
          v-else-if="errorUsers"
          class="list-error"
        >
          <i class="fa fa-exclamation-circle mr-1" />
          {{ errorUsers }}
        </div>

        <div
          v-else-if="!users.length"
          class="list-empty"
        >
          Sin actividad registrada
        </div>

        <div
          v-else-if="!filteredUsers.length"
          class="list-empty"
        >
          Ningún usuario coincide con la búsqueda
        </div>

        <div
          v-for="user in filteredUsers"
          :key="user.id"
          class="user-row"
        >
          <div
            class="user-avatar"
            :style="avatarStyle(user.name)"
          >
            {{ initials(user.name) }}
          </div>
          <span class="user-name">{{ user.name }}</span>
          <span class="user-count">{{ user.occurrences }}</span>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="panel-header detail-header">
        <button
          type="button"
          class="back-btn"
          aria-label="Volver a la lista"
          @click="clearSelection"
        >
          <i class="fa fa-arrow-left" />
        </button>
        <div class="detail-header-text">
          <span class="panel-title">{{ selectedUser.name }}</span>
          <span class="panel-subtitle">Indicadores (demo)</span>
        </div>
      </div>

      <div
        v-if="selectedInsights"
        class="detail-body"
      >
        <section class="insight-section">
          <h4 class="insight-section-title">
            Meta del mes
          </h4>
          <div class="insight-hero">
            {{ selectedInsights.visitedThisMonth }}
            <span class="insight-hero-sep">/</span>
            {{ selectedInsights.monthlyGoal }}
            <span class="insight-hero-unit">tiendas</span>
          </div>
          <div class="insight-progress-track">
            <div
              class="insight-progress-fill"
              :class="monthProgressClass"
              :style="{ width: monthProgressPct + '%' }"
            />
          </div>
          <p class="insight-hint">
            Mes en curso · objetivo de visitas
          </p>
        </section>

        <section class="insight-grid">
          <div class="insight-tile">
            <i class="fa fa-map-marker tile-icon" />
            <div class="tile-value">
              {{ selectedInsights.storesVisitedToday }}
            </div>
            <div class="tile-label">
              Visitadas hoy
            </div>
          </div>
          <div class="insight-tile">
            <i class="fa fa-line-chart tile-icon" />
            <div class="tile-value">
              {{ selectedInsights.avgStoresPerDay }}
            </div>
            <div class="tile-label">
              Promedio / día
            </div>
          </div>
          <div class="insight-tile insight-tile--wide">
            <i class="fa fa-clock-o tile-icon" />
            <div class="tile-value">
              {{ selectedInsights.avgStayInStore }}
            </div>
            <div class="tile-label">
              Permanencia promedio en tienda
            </div>
          </div>
        </section>

        <section class="insight-section route-section">
          <h4 class="insight-section-title">
            Ruta sugerida
          </h4>
          <p class="route-intro">
            Próximos 2 días
          </p>
          <div
            v-for="(day, idx) in routeDaysWithLabels"
            :key="idx"
            class="route-day-card"
          >
            <div class="route-day-title">
              <span class="route-day-title-text">{{ day.label }}</span>
              <button
                v-b-tooltip.hover
                type="button"
                class="route-map-btn"
                title="Ver ruta en mapa"
                @click="openRouteMap(day)"
              >
                <i class="fa fa-map" />
              </button>
            </div>
            <ol class="route-store-list">
              <li
                v-for="store in day.stores"
                :key="store.id"
                class="route-store-item"
              >
                {{ store.name }}
              </li>
            </ol>
          </div>
        </section>
      </div>
    </template>

    <BaseModal
      :show="showRouteMapModal"
      :title="routeMapModalTitle"
      size="lg"
      :show-confirm="false"
      cancel-text="Cerrar"
      @update:show="showRouteMapModal = $event"
    >
      <p class="route-map-caption mb-2">
        Tiendas planificadas para este día
      </p>
      <div
        v-if="routeMapMounted && routeMapStores.length"
        class="route-map-container"
      >
        <GmapMap
          :center="routeMapCenter"
          :zoom="routeMapZoom"
          :options="routeMapOptions"
          class="route-gmap"
        >
          <GmapMarker
            v-for="store in routeMapStores"
            :key="store.id"
            :position="{ lat: store.latitude, lng: store.longitude }"
            :title="store.name"
          />
        </GmapMap>
      </div>
      <p
        v-else-if="routeMapMounted && !routeMapStores.length"
        class="text-center text-muted py-4 mb-0"
      >
        No hay tiendas para este día
      </p>
    </BaseModal>
  </div>
</template>

<script>
import { ref, computed } from '@vue/composition-api'
import BaseModal from '@/app/shared/components/BaseModal.vue'
import { useFieldInsights } from '../compositions/useFieldInsights'

const AVATAR_COLORS = [
  '#4299e1', '#48bb78', '#ed8936', '#9f7aea',
  '#f56565', '#38b2ac', '#ed64a6', '#667eea'
]

export default {
  name: 'FieldInsightsPanel',
  components: {
    BaseModal
  },
  setup () {
    const userSearchQuery = ref('')
    const {
      users,
      selectedUser,
      selectedInsights,
      loadingUsers,
      errorUsers,
      fetchUsers,
      selectUser,
      clearSelection
    } = useFieldInsights()

    const filteredUsers = computed(() => {
      const q = userSearchQuery.value.trim().toLowerCase()
      if (!q) return users.value
      return users.value.filter(u => (u.name || '').toLowerCase().includes(q))
    })

    fetchUsers()

    return {
      userSearchQuery,
      filteredUsers,
      users,
      selectedUser,
      selectedInsights,
      loadingUsers,
      errorUsers,
      selectUser,
      clearSelection
    }
  },
  data () {
    return {
      showRouteMapModal: false,
      routeMapModalTitle: '',
      routeMapStores: [],
      routeMapMounted: false
    }
  },
  computed: {
    routeMapCenter () {
      const list = this.routeMapStores
      if (!list.length) return { lat: -33.4489, lng: -70.6693 }
      const lat = list.reduce((a, s) => a + s.latitude, 0) / list.length
      const lng = list.reduce((a, s) => a + s.longitude, 0) / list.length
      return { lat, lng }
    },
    routeMapZoom () {
      const n = this.routeMapStores.length
      if (n <= 1) return 13
      if (n <= 4) return 12
      return 11
    },
    routeMapOptions () {
      return {
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        gestureHandling: 'greedy'
      }
    },
    monthProgressPct () {
      const s = this.selectedInsights
      if (!s || !s.monthlyGoal) return 0
      return Math.min(100, Math.round((s.visitedThisMonth / s.monthlyGoal) * 100))
    },
    monthProgressClass () {
      const p = this.monthProgressPct
      if (p >= 90) return 'fill--green'
      if (p >= 60) return 'fill--blue'
      return 'fill--amber'
    },
    routeDaysWithLabels () {
      const s = this.selectedInsights
      if (!s || !s.routes) return []
      return s.routes.map(r => {
        return {
          label: this.$moment().add(r.offset, 'day').format('dddd D [de] MMMM'),
          stores: r.stores
        }
      })
    }
  },
  watch: {
    showRouteMapModal (open) {
      if (open) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.routeMapMounted = true
          }, 200)
        })
      } else {
        this.routeMapMounted = false
      }
    }
  },
  methods: {
    openRouteMap (day) {
      this.routeMapModalTitle = `Ruta · ${day.label}`
      this.routeMapStores = day.stores && day.stores.length ? [...day.stores] : []
      this.showRouteMapModal = true
    },
    initials (name) {
      if (!name) return '?'
      return name.trim().split(' ').slice(0, 2).map(p => p[0]).join('').toUpperCase()
    },
    avatarStyle (name) {
      const idx = (name || '').charCodeAt(0) % AVATAR_COLORS.length
      return { background: AVATAR_COLORS[idx] }
    }
  }
}
</script>

<style scoped>
.field-insights-panel {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  overflow: hidden;
  max-height: calc(100vh - 55px - 2.5rem);
  display: flex;
  flex-direction: column;
}

.panel-header {
  padding: 0.875rem 1rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex-shrink: 0;
}

.detail-header {
  flex-direction: row;
  align-items: center;
  gap: 0.625rem;
}

.detail-header-text {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.back-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  color: #4a5568;
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  transition: background 0.15s;
}

.back-btn:hover {
  background: #edf2f7;
}

.panel-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #2d3748;
}

.detail-header .panel-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.panel-subtitle {
  font-size: 0.6875rem;
  color: #a0aec0;
}

.search-wrap {
  position: relative;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.search-wrap .search-icon {
  position: absolute;
  left: 1.15rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.7rem;
  color: #a0aec0;
  pointer-events: none;
}

.user-search-input {
  width: 100%;
  padding: 0.4rem 1.75rem 0.4rem 1.75rem;
  font-size: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #2d3748;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.user-search-input:focus {
  border-color: #4299e1;
  background: #fff;
  box-shadow: 0 0 0 2px rgba(66, 153, 225, 0.12);
}

.user-search-input::placeholder {
  color: #cbd5e0;
}

.search-clear {
  position: absolute;
  right: 0.85rem;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: none;
  padding: 0.2rem;
  color: #a0aec0;
  cursor: pointer;
  font-size: 0.65rem;
  line-height: 1;
  display: flex;
  align-items: center;
}

.search-clear:hover {
  color: #4a5568;
}

.panel-body {
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.detail-body {
  padding: 0.75rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.insight-section-title {
  font-size: 0.6875rem;
  font-weight: 600;
  color: #a0aec0;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0 0 0.5rem 0;
}

.insight-hero {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
  line-height: 1.2;
}

.insight-hero-sep {
  color: #cbd5e0;
  font-weight: 400;
  margin: 0 0.15rem;
}

.insight-hero-unit {
  font-size: 0.75rem;
  font-weight: 500;
  color: #718096;
  margin-left: 0.25rem;
}

.insight-progress-track {
  height: 6px;
  background: #edf2f7;
  border-radius: 3px;
  margin-top: 0.5rem;
  overflow: hidden;
}

.insight-progress-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.35s ease;
}

.fill--green { background: #48bb78; }
.fill--blue { background: #4299e1; }
.fill--amber { background: #ed8936; }

.insight-hint {
  font-size: 0.6875rem;
  color: #a0aec0;
  margin: 0.35rem 0 0 0;
}

.insight-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
}

.insight-tile {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.65rem 0.75rem;
  text-align: center;
}

.insight-tile--wide {
  grid-column: 1 / -1;
  text-align: left;
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto auto;
  column-gap: 0.75rem;
  align-items: center;
}

.insight-tile--wide .tile-icon {
  grid-row: 1 / 3;
  font-size: 1.25rem;
  color: #4299e1;
}

.insight-tile--wide .tile-value {
  grid-column: 2;
}

.insight-tile--wide .tile-label {
  grid-column: 2;
}

.tile-icon {
  font-size: 0.875rem;
  color: #4299e1;
  margin-bottom: 0.25rem;
  display: block;
}

.tile-value {
  font-size: 1.125rem;
  font-weight: 700;
  color: #2d3748;
}

.tile-label {
  font-size: 0.65rem;
  color: #718096;
  margin-top: 0.15rem;
  line-height: 1.25;
}

.route-intro {
  font-size: 0.75rem;
  color: #718096;
  margin: -0.25rem 0 0.5rem 0;
}

.route-day-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 0.5rem;
  background: white;
}

.route-day-card:last-child {
  margin-bottom: 0;
}

.route-day-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #4a5568;
  padding: 0.5rem 0.65rem;
  background: #f8fafc;
  border-bottom: 1px solid #edf2f7;
  text-transform: capitalize;
}

.route-day-title-text {
  min-width: 0;
  flex: 1;
  line-height: 1.3;
}

.route-map-btn {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  color: #4299e1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.route-map-btn:hover {
  background: rgba(66, 153, 225, 0.08);
  border-color: #4299e1;
  color: #2b6cb0;
}

.route-map-caption {
  font-size: 0.8125rem;
  color: #718096;
  margin: 0;
}

.route-map-container {
  width: 100%;
  height: min(420px, 55vh);
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.route-gmap {
  width: 100%;
  height: 100%;
  min-height: 320px;
}

.route-store-list {
  margin: 0;
  padding: 0.4rem 0.65rem 0.5rem 1.35rem;
  font-size: 0.75rem;
  color: #4a5568;
  line-height: 1.45;
}

.route-store-item {
  padding: 0.15rem 0;
}

.user-row {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5rem 0.625rem;
  border-radius: 8px;
  border: none;
  background: transparent;
  cursor: default;
  transition: background 0.15s ease;
  text-align: left;
  width: 100%;
}

.user-row:hover {
  background: #f8fafc;
}

.user-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.6875rem;
  font-weight: 600;
}

.user-name {
  flex: 1;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #2d3748;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.user-count {
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
  background: #4299e1;
  border-radius: 10px;
  padding: 1px 7px;
  flex-shrink: 0;
  line-height: 1.6;
}

.list-error {
  font-size: 0.8125rem;
  color: #e53e3e;
  padding: 0.75rem;
  text-align: center;
}

.list-empty {
  font-size: 0.8125rem;
  color: #a0aec0;
  padding: 0.75rem;
  text-align: center;
}

.user-row-skeleton {
  height: 40px;
  border-radius: 8px;
  margin-bottom: 2px;
}

@keyframes shimmer {
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
}

.shimmer {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: shimmer 1.5s infinite;
}
</style>



// WEBPACK FOOTER //
// src/app/news_feed/components/FieldInsightsPanel.vue